import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Shield, Award, Star, MapPin, Dumbbell, Heart } from 'lucide-react';
import { getInitials, CATEGORY_NAMES, formatCurrency } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { addFavoriteTrainer, removeFavoriteTrainer } from '../lib/api';
import BookingDialog from './BookingDialog';
import { toast } from 'sonner';

const VERIFICATION_CONFIG = {
  verified: { icon: Shield, label: 'Professional', className: 'badge-verified' },
  elite: { icon: Award, label: 'Elite', className: 'badge-elite' },
  specialist: { icon: Star, label: 'Specialist', className: 'badge-specialist' },
};

const TrainerCard = ({ trainer }) => {
  const navigate = useNavigate();
  const { user, isClient, refreshUser } = useAuth();
  const profile = trainer.trainer_profile || {};
  const verification = VERIFICATION_CONFIG[profile.verification_level] || VERIFICATION_CONFIG.verified;
  const VerificationIcon = verification.icon;
  const topCredential = (profile.certifications || []).find((c) => c && c.trim()) || null;
  const bringsEquipment = profile.equipment === 'full' || profile.equipment === 'some';

  const initialFav = (user?.client_profile?.favorite_trainer_ids || []).includes(trainer.id);
  const [isFavorite, setIsFavorite] = useState(initialFav);
  const [bookingOpen, setBookingOpen] = useState(false);
  useEffect(() => { setIsFavorite((user?.client_profile?.favorite_trainer_ids || []).includes(trainer.id)); }, [user, trainer.id]);

  const toggleFavorite = async (e) => {
    e.stopPropagation();
    if (!isClient) { toast.message('Sign in as a client to save coaches.'); return; }
    try {
      if (isFavorite) {
        await removeFavoriteTrainer(trainer.id);
        setIsFavorite(false);
      } else {
        await addFavoriteTrainer(trainer.id);
        setIsFavorite(true);
      }
      refreshUser?.();
    } catch (err) {
      toast.error('Could not update favorites');
    }
  };

  const renderStars = (rating) => Array.from({ length: 5 }, (_, i) => (
    <Star
      key={i}
      className={`w-4 h-4 ${i < Math.round(rating) ? 'star-filled fill-current' : 'star-empty'}`}
    />
  ));

  return (
    <div
      className="trainer-card bg-[#18181B] border border-zinc-800 rounded-lg p-6 cursor-pointer relative"
      onClick={() => navigate(`/trainers/${trainer.id}`)}
      data-testid={`trainer-card-${trainer.id}`}
    >
      {isClient && (
        <button
          type="button"
          onClick={toggleFavorite}
          className={`absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center transition-colors ${
            isFavorite ? 'bg-[#C0A062]/15 text-[#C0A062]' : 'bg-zinc-800/60 text-zinc-400 hover:text-[#C0A062]'
          }`}
          aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
          data-testid={`favorite-toggle-${trainer.id}`}
        >
          <Heart className={`w-4 h-4 ${isFavorite ? 'fill-current' : ''}`} />
        </button>
      )}
      <div className="flex items-start gap-4 mb-3">
        <Avatar className="w-16 h-16 border-2 border-[#C0A062]/30">
          <AvatarImage src={profile.profile_image} />
          <AvatarFallback className="bg-[#27272A] text-[#C0A062] text-lg font-bold">
            {getInitials(trainer.full_name)}
          </AvatarFallback>
        </Avatar>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <h3 className="text-lg font-bold text-white truncate">{trainer.full_name}</h3>
            <Badge className={`${verification.className} text-xs px-2 py-0.5 rounded-full flex items-center gap-1`}>
              <VerificationIcon className="w-3 h-3" />
              {verification.label}
            </Badge>
            {profile.background_check_status === 'cleared' && (
              <Badge
                className="bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 text-xs px-2 py-0.5 rounded-full flex items-center gap-1"
                data-testid={`bg-check-badge-${trainer.id}`}
              >
                <Shield className="w-3 h-3" />
                Background Check Cleared
              </Badge>
            )}
          </div>

          {trainer.city && (
            <div className="flex items-center gap-1 text-zinc-400 text-sm">
              <MapPin className="w-3 h-3" />
              {trainer.city}
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 mb-3 flex-wrap min-h-[26px]">
        {topCredential && (
          <span
            className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-full bg-[#C0A062]/15 border border-[#C0A062]/40 text-[#C0A062] max-w-full truncate"
            data-testid={`top-credential-${trainer.id}`}
            title={topCredential}
          >
            <Award className="w-3 h-3 shrink-0" />
            <span className="truncate">{topCredential}</span>
          </span>
        )}
        {bringsEquipment && (
          <span
            className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-300"
            data-testid={`equipment-badge-${trainer.id}`}
          >
            <Dumbbell className="w-3 h-3" />
            Brings Equipment
          </span>
        )}
      </div>

      <div className="flex items-center gap-2 mb-4">
        <div className="flex">{renderStars(trainer.avg_rating || 0)}</div>
        <span className="text-sm text-zinc-400">({trainer.review_count || 0} reviews)</span>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        {(profile.categories || []).map((cat) => (
          <span key={cat} className="text-xs px-2 py-1 bg-zinc-800 text-zinc-300 rounded">
            {CATEGORY_NAMES[cat] || cat}
          </span>
        ))}
      </div>

      {profile.bio && (
        <p className="text-sm text-zinc-400 line-clamp-2 mb-4">{profile.bio}</p>
      )}

      <div className="flex items-center justify-between pt-4 border-t border-zinc-800">
        <div className="text-sm">
          <span className="text-zinc-400">From </span>
          <span className="text-[#C0A062] font-bold">
            {formatCurrency(profile.hourly_rate || 75)}/session
          </span>
        </div>
        <div className="flex items-center gap-2">
          {trainer.is_accepting_bookings === false ? (
            <span
              className="text-[10px] font-semibold px-2 py-1 rounded-full bg-zinc-800 text-zinc-400 border border-zinc-700"
              data-testid={`not-accepting-bookings-${trainer.id}`}
              title="This coach hasn't connected payouts yet"
            >
              Not accepting bookings yet
            </span>
          ) : (
            <Button
              size="sm"
              variant="outline"
              className="border-[#C0A062]/50 text-[#C0A062] hover:bg-[#C0A062]/10 hover:text-[#C0A062] text-xs bg-transparent"
              onClick={(e) => { e.stopPropagation(); setBookingOpen(true); }}
              data-testid={`book-trainer-${trainer.id}`}
            >
              Book
            </Button>
          )}
          <Button
            size="sm"
            className="btn-primary text-xs"
            data-testid={`view-trainer-${trainer.id}`}
          >
            View Profile
          </Button>
        </div>
      </div>

      <div onClick={(e) => e.stopPropagation()}>
        <BookingDialog
          open={bookingOpen}
          onOpenChange={setBookingOpen}
          trainer={trainer}
          bookingType={trainer.is_available_now ? 'instant' : 'scheduled'}
        />
      </div>
    </div>
  );
};

export default TrainerCard;
